import {FEATURE_FLAGS} from '../../../constants';

export enum EntityType {
	PROJECT = 'PROJECT',
	PHASE = 'PHASE',
	TASK = 'TASK',
	TIME_REG = 'TIME_REG',
	TICKET = 'TICKET',
}

export enum CustomFieldType {
	STRING = 'STRING',
	DROPDOWN = 'DROPDOWN',
	INTEGER = 'INTEGER',
	DECIMAL = 'DECIMAL',
	DATE = 'DATE',
	BOOLEAN = 'BOOLEAN',
	HYPERLINK = 'HYPERLINK',
	CURRENCY = 'CURRENCY',
	MULTI_SELECT = 'MULTI_SELECT',
}

export const EntityTypeLabels: Record<EntityType, string> = {
	[EntityType.PROJECT]: 'Project',
	[EntityType.PHASE]: 'Phase',
	[EntityType.TASK]: 'Task',
	[EntityType.TIME_REG]: 'Time Entry',
	[EntityType.TICKET]: 'Ticket',
};

// The message for each type's name, wherever an admin sees it: the type picker, the list and the details.
// Every type must be listed, so adding a type without naming it fails the type check.
export const CustomFieldTypeLabelIds: Record<CustomFieldType, string> = {
	[CustomFieldType.STRING]: 'filters.custom_field.type.text',
	[CustomFieldType.DROPDOWN]: 'filters.custom_field.type.dropdown',
	[CustomFieldType.INTEGER]: 'filters.custom_field.type.integer',
	[CustomFieldType.DECIMAL]: 'filters.custom_field.type.decimal',
	[CustomFieldType.DATE]: 'filters.custom_field.type.date',
	[CustomFieldType.BOOLEAN]: 'custom_field.type.boolean',
	[CustomFieldType.HYPERLINK]: 'custom_field.type.hyperlink',
	[CustomFieldType.CURRENCY]: 'custom_field.type.currency',
	[CustomFieldType.MULTI_SELECT]: 'custom_field.type.multi_select',
};

// The feature flag each type needs before it appears in the type picker, or null for none.
// Every type must be listed, so adding a type without deciding its flag fails the type check.
export const CUSTOM_FIELD_TYPE_FEATURE_FLAG: Record<CustomFieldType, string | null> = {
	[CustomFieldType.STRING]: null,
	[CustomFieldType.DROPDOWN]: null,
	[CustomFieldType.INTEGER]: FEATURE_FLAGS.INTEGER_DECIMAL_DATE_CUSTOM_FIELDS,
	[CustomFieldType.DECIMAL]: FEATURE_FLAGS.INTEGER_DECIMAL_DATE_CUSTOM_FIELDS,
	[CustomFieldType.DATE]: FEATURE_FLAGS.INTEGER_DECIMAL_DATE_CUSTOM_FIELDS,
	[CustomFieldType.BOOLEAN]: FEATURE_FLAGS.MORE_CUSTOM_FIELD_TYPES,
	[CustomFieldType.HYPERLINK]: FEATURE_FLAGS.MORE_CUSTOM_FIELD_TYPES,
	[CustomFieldType.CURRENCY]: FEATURE_FLAGS.MORE_CUSTOM_FIELD_TYPES,
	[CustomFieldType.MULTI_SELECT]: FEATURE_FLAGS.MORE_CUSTOM_FIELD_TYPES,
};

// Whether a field of this type offers the read-only setting. A Dropdown's and a Multi-select's values are
// chosen from their options, so they have none. Every type must be listed, so adding a type without deciding
// this fails the type check.
const SUPPORTS_READ_ONLY: Record<CustomFieldType, boolean> = {
	[CustomFieldType.STRING]: true,
	[CustomFieldType.DROPDOWN]: false,
	[CustomFieldType.INTEGER]: true,
	[CustomFieldType.DECIMAL]: true,
	[CustomFieldType.DATE]: true,
	[CustomFieldType.BOOLEAN]: true,
	[CustomFieldType.HYPERLINK]: true,
	[CustomFieldType.CURRENCY]: true,
	[CustomFieldType.MULTI_SELECT]: false,
};

export const supportsReadOnly = (type: CustomFieldType): boolean => SUPPORTS_READ_ONLY[type];

// Whether a field of this type takes its values from options the admin manages in the options modal: a
// Dropdown picks one of them, a Multi-select any number.
export const hasOptionList = (type: CustomFieldType): boolean =>
	type === CustomFieldType.DROPDOWN || type === CustomFieldType.MULTI_SELECT;
